import React from "react";
import { cn } from "@/lib/utils";
import {
  designTokens,
  notificationSeverityTokens,
  type NotificationSeverityName,
} from "@/lib/tokens";

export interface GlobalBannerProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  tone?: NotificationSeverityName;
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
  className?: string;
}

export const GlobalBanner: React.FC<GlobalBannerProps> = ({
  tone = "info",
  title,
  description,
  icon,
  actions,
  onDismiss,
  dismissLabel = "Dismiss",
  className,
  ...rest
}) => {
  const tokens = notificationSeverityTokens[tone];
  const accent = tone === "critical" ? designTokens.accentRule.brand : tokens.accent;

  return (
    <aside
      className={cn(
        "w-full border-b px-4 py-3 sm:px-6",
        "bg-white shadow-sm",
        className,
      )}
      style={{ borderColor: tokens.border }}
      role="status"
      aria-live="polite"
      {...rest}
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex items-start gap-3">
          {icon ? (
            <div
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white"
              style={{ color: accent, border: `1px solid ${tokens.border}` }}
            >
              {icon}
            </div>
          ) : null}
          <div className="space-y-1">
            <div className="text-sm font-semibold text-slate-900">{title}</div>
            {description ? (
              <div className="text-sm text-slate-700">{description}</div>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 sm:ml-auto">
          {actions}
          {onDismiss ? (
            <button
              type="button"
              className="text-sm font-medium text-slate-600 hover:text-slate-900"
              onClick={onDismiss}
            >
              {dismissLabel}
            </button>
          ) : null}
        </div>
      </div>
    </aside>
  );
};

GlobalBanner.displayName = "GlobalBanner";
